'use client';

// Twitter/X-style preview card for YouTube URLs found in a post body.
// Clicking opens the watch URL in a new tab. Image and title come from
// oEmbed (cached server-side in link_previews); if those fail we still
// render with the deterministic hqdefault thumbnail and a placeholder
// title so the feed never crashes on a deleted/private video.

import { Play, Youtube as YoutubeIcon } from 'lucide-react';
import { buildWatchUrl } from '@/lib/youtube';

export interface YouTubePreviewProps {
  videoId: string;
  title: string;
  authorName: string;
  thumbnailUrl: string;
}

export default function YouTubePreview({
  videoId,
  title,
  authorName,
  thumbnailUrl,
}: YouTubePreviewProps) {
  const href = buildWatchUrl(videoId);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="block overflow-hidden rounded-md border border-border-subtle bg-surface-1 hover:bg-surface-3 transition-colors duration-150 max-w-md"
    >
      <div className="relative aspect-video w-full bg-black">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={thumbnailUrl}
          alt={title || 'YouTube video'}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/70 ring-1 ring-white/20">
            <Play
              className="h-5 w-5 translate-x-[1px] text-white"
              fill="currentColor"
              strokeWidth={0}
            />
          </div>
        </div>
        <div className="absolute left-2 top-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
          <YoutubeIcon className="h-3 w-3" strokeWidth={2} />
          YouTube
        </div>
      </div>
      <div className="p-2.5">
        <div className="text-sm font-medium text-text-primary line-clamp-2">
          {title || 'YouTube video'}
        </div>
        {authorName && (
          <div className="mt-0.5 text-xs text-text-tertiary truncate">{authorName}</div>
        )}
      </div>
    </a>
  );
}
